import { useState, useEffect, useCallback } from 'react'
import clsx from 'clsx'
import { Folder, ChevronRight, MatterFolder } from '@/assets/icons'
import { listAll, StorageReference } from 'firebase/storage'
import { CircularProgressContinuous } from '@/components/loaders/CircularProgressContinuous'
import { EmptyFolder } from '@/assets/illustration'
import { renderFolderName } from '../utils/folder-name-utils'
import { validate as isValidUUID } from 'uuid'

type FolderPickerProps = {
  currentFolderRef: StorageReference | null
  onNavigate: (folderRef: StorageReference) => void
  renderedMatterNameMap: Map<string, string>
}

type DisplayFolder = {
  folderRef: StorageReference
  displayName: string
}

export function FolderPicker({ currentFolderRef, onNavigate, renderedMatterNameMap }: FolderPickerProps) {
  const [folders, setFolders] = useState<DisplayFolder[]>([])
  const [loading, setLoading] = useState(false)

  const fetchFolders = useCallback(
    async (folderRef: StorageReference) => {
      setLoading(true)
      try {
        const response = await listAll(folderRef)

        const displayFolders: DisplayFolder[] = response.prefixes.map((folderRef) => {
          const displayName = renderFolderName(folderRef.name, folderRef, renderedMatterNameMap)
          return { folderRef, displayName }
        })

        displayFolders.sort((a, b) => a.displayName.localeCompare(b.displayName))

        setFolders(displayFolders)
      } catch {
        setFolders([])
      } finally {
        setLoading(false)
      }
    },
    [renderedMatterNameMap]
  )

  useEffect(() => {
    if (currentFolderRef) {
      fetchFolders(currentFolderRef)
    }
  }, [currentFolderRef, fetchFolders])

  const isMatterFolder = (folder: DisplayFolder) => isValidUUID(folder.folderRef.name) && renderedMatterNameMap.has(folder.folderRef.name)

  if (!currentFolderRef) {
    return (
      <div className="flex items-center justify-center p-8 text-brand-neutral-500">
        <span>Loading...</span>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto min-h-0" aria-label="Folder contents">
        {loading ? (
          <div className="flex items-center justify-center p-8">
            <CircularProgressContinuous />
          </div>
        ) : (
          <>
            {folders.length === 0 ? (
              <div className="h-full px-3 py-8 text-center text-sm text-brand-neutral-500 font-medium flex flex-col items-center justify-center">
                <EmptyFolder className="mx-auto h-10 w-10 text-brand-neutral-500 mb-2" aria-hidden="true" />
                <span>There are no subfolders in this folder</span>
              </div>
            ) : (
              folders.map((folder) => {
                const isMatter = isMatterFolder(folder)
                const FolderIcon = isMatter ? MatterFolder : Folder

                return (
                  <div
                    key={folder.folderRef.fullPath}
                    className={clsx(
                      'py-3 px-6 first:border-t-0 transition-colors border-b border-brand-neutral-200 hover:bg-brand-neutral-100',
                      folders.length >= 7 && 'last:border-b-0'
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => onNavigate(folder.folderRef)}
                      className="w-full text-left flex items-center gap-2 text-ellipsis"
                      aria-label={`Open folder ${folder.displayName}`}
                    >
                      <FolderIcon className="flex-shrink-0 mr-2 h-5 w-5 text-brand-neutral-950" aria-hidden="true" />
                      <span className="text-ellipsis overflow-hidden whitespace-nowrap">{folder.displayName}</span>
                      <ChevronRight className="flex-shrink-0 ml-auto h-4 w-4 text-brand-neutral-950" aria-hidden="true" />
                    </button>
                  </div>
                )
              })
            )}
          </>
        )}
      </div>
    </div>
  )
}
